<script setup lang="ts">
// Low-level file picker. Renders a hidden <input type="file"> and exposes
// `pick` / `clear` to the default slot — the caller composes its own visible
// trigger from kit citizens (Button, Card, etc.). v-model binds the chosen
// File or null. Caller can validate / format size via the slot's `file` arg.
import { ref } from 'vue'

interface Props {
  modelValue: File | null
  /** File-type filter — e.g. `.pdf,.png,image/*`. */
  accept?: string
}

defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: File | null): void
}>()

const inputRef = ref<HTMLInputElement | null>(null)

// Clearing before opening the picker is what makes re-choosing the *same* file
// register: the browser only fires `change` when the input's value actually
// changes, so without this a "replace" that lands on the identical file is
// silently swallowed and the caller never hears about it.
const pick = () => {
  if (!inputRef.value) return
  inputRef.value.value = ''
  inputRef.value.click()
}

const clear = () => {
  emit('update:modelValue', null)
  if (inputRef.value) inputRef.value.value = ''
}

// Lets a parent open the picker from outside the slot — e.g. a "Re-upload"
// button in a dialog. Must run inside a click handler: browsers only open a file
// picker in response to a user gesture.
defineExpose({ pick })

const onChange = (event: Event) => {
  const input = event.target
  if (!(input instanceof HTMLInputElement)) return
  emit('update:modelValue', input.files?.[0] ?? null)
}
</script>

<template>
  <slot :pick="pick" :clear="clear" :file="modelValue" />
  <input ref="inputRef" type="file" :accept="accept" class="app-file-input" @change="onChange" />
</template>

<style scoped>
.app-file-input {
  display: none;
}
</style>
